import React, { Suspense, lazy } from 'react';
import { useRoutes, Navigate } from 'react-router-dom';
import { CircularProgress, Box } from '@mui/material';

import ProtectedRoute from './components/ProtectedRoute';
import { MEDIA_MANAGER_ROLE } from './utils/access';

// Eagerly loaded (small, always needed)
import Splash from './pages/Splash';

// Lazy-loaded pages
const Home = lazy(() => import('./pages/Home'));
const Profile = lazy(() => import('./pages/Profile'));
const TakePhoto = lazy(() => import('./pages/TakePhoto'));

const AdminLayout = lazy(() => import('./layouts/AdminLayout'));
const ManagerLayout = lazy(() => import('./layouts/ManagerLayout'));
const MediaLayout = lazy(() => import('./layouts/MediaLayout'));
const MediaWorkspace = lazy(() => import('./pages/media/MediaWorkspace'));

const Users = lazy(() => import('./pages/admin/User'));
const Agency = lazy(() => import('./pages/admin/Agency'));
const Client = lazy(() => import('./pages/admin/Client'));
const Sites = lazy(() => import('./pages/admin/Site'));
const Finance = lazy(() => import('./pages/admin/Finance'));
const AdTypes = lazy(() => import('./pages/admin/AdType'));
const Photo = lazy(() => import('./pages/admin/Photo'));
const Photos = lazy(() => import('./pages/admin/Photos'));
const Campaign = lazy(() => import('./pages/admin/Campaign'));
const MediaOrder = lazy(() => import('./pages/admin/MediaOrder'));
const Offer = lazy(() => import('./pages/admin/Offer'));
const Terminal = lazy(() => import('./pages/admin/Terminal'));
const Airport = lazy(() => import('./pages/admin/Airport'));
const Currencies = lazy(() => import('./pages/admin/Currencies'));
const Invoice = lazy(() => import('./pages/admin/Invoice'));
const PhotoReport = lazy(() => import('./pages/admin/PhotoReport'));
const CreateMockups = lazy(() => import('./pages/admin/CreateMockups'));

const PresentationCatalog = lazy(() => import('./pages/presentation/AllPresentation'));
const Presentation = lazy(() => import('./pages/presentation/Presentation'));
const SiteLayout = lazy(() => import('./pages/presentation/SiteLayout'));

const LazyFallback = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
    <CircularProgress />
  </Box>
);

export const AppRoutes = ({isAuthenticated}) => {
  const routes = [
    // Public routes
    { path: '/', element: <Splash /> },
    { path: '/presentation/:terminal',
      element: <Suspense fallback={<LazyFallback />}><Presentation /></Suspense>,
      children:[
        {
          path: 'site/:pageId',
          element: <Suspense fallback={<LazyFallback />}><SiteLayout /></Suspense>
        },
      ]
    },
    { path: '/presentation/site/:site', element: <Suspense fallback={<LazyFallback />}><SiteLayout /></Suspense> },

    // Protected routes for all authenticated users
    {
      path: '/media',
      element: <ProtectedRoute allowedRoles={[1, MEDIA_MANAGER_ROLE]}><Suspense fallback={<LazyFallback />}><MediaLayout /></Suspense></ProtectedRoute>,
      children: [{ index: true, element: <Suspense fallback={<LazyFallback />}><MediaWorkspace /></Suspense> }]
    },
    {
      path: '/home',
      element: <ProtectedRoute><Suspense fallback={<LazyFallback />}><Home /></Suspense></ProtectedRoute>
    },
    {
      path: '/profile',
      element: <ProtectedRoute><Suspense fallback={<LazyFallback />}><Profile /></Suspense></ProtectedRoute>
    },
    {
      path: '/takephoto',
      element: <ProtectedRoute><Suspense fallback={<LazyFallback />}><TakePhoto /></Suspense></ProtectedRoute>
    },

    // Admin/PJM routes (userType 1 and userType 3)
    {
      path: '/admin',
      element: <ProtectedRoute allowedRoles={[1, 3]}><Suspense fallback={<LazyFallback />}><AdminLayout /></Suspense></ProtectedRoute>,
      children: [
        {
          index: true,
          element: <Suspense fallback={<LazyFallback />}><Offer /></Suspense>
        },
        {
          path: 'users',
          element: <Suspense fallback={<LazyFallback />}><Users /></Suspense>
        },
        {
          path: 'agency',
          element: <Suspense fallback={<LazyFallback />}><Agency /></Suspense>
        },
        {
          path: 'client',
          element: <Suspense fallback={<LazyFallback />}><Client /></Suspense>
        },
        {
          path: 'site',
          children: [
            {
              path: 'sites',
              element: <Suspense fallback={<LazyFallback />}><Sites /></Suspense>
            },
            {
              path: 'types',
              element: <Suspense fallback={<LazyFallback />}><AdTypes /></Suspense>
            },
          ]
        },
        {
          path: 'sites/photo',
          element: <Suspense fallback={<LazyFallback />}><Photo /></Suspense>
        },
        {
          path: 'offer',
          element: <Suspense fallback={<LazyFallback />}><Offer /></Suspense>
        },
        {
          path: 'media-order',
          element: <Suspense fallback={<LazyFallback />}><MediaOrder /></Suspense>
        },
        {
          path: 'invoice',
          element: <Suspense fallback={<LazyFallback />}><Invoice /></Suspense>
        },
        {
          path: 'campaign',
          element: <Suspense fallback={<LazyFallback />}><Campaign /></Suspense>
        },
        {
          path: 'photo-report',
          element: <Suspense fallback={<LazyFallback />}><PhotoReport /></Suspense>
        },
        {
          path: 'photos',
          element: <Suspense fallback={<LazyFallback />}><Photos /></Suspense>
        },
        {
          path: 'presentation',
          element: <Suspense fallback={<LazyFallback />}><PresentationCatalog /></Suspense>
        },
        {
          path: 'create-mockups',
          element: <Suspense fallback={<LazyFallback />}><CreateMockups /></Suspense>
        },
        {
          path: 'terminal',
          element: <Suspense fallback={<LazyFallback />}><Terminal /></Suspense>
        },
        {
          path: 'airport',
          element: <Suspense fallback={<LazyFallback />}><Airport /></Suspense>
        },
        {
          path: 'currencies',
          element: <Suspense fallback={<LazyFallback />}><Currencies /></Suspense>
        }
      ]
    },

    // Manager routes (userType 2)
    {
      path: '/manager',
      element: <ProtectedRoute allowedRoles={[1, 2, 3]}><Suspense fallback={<LazyFallback />}><ManagerLayout /></Suspense></ProtectedRoute>,
      children: [
        {
          index: true,
          element: <Suspense fallback={<LazyFallback />}><Terminal /></Suspense>
        },
        {
          path: 'terminal',
          element: <Suspense fallback={<LazyFallback />}><Terminal /></Suspense>
        },
        {
          path: 'users',
          element: <Suspense fallback={<LazyFallback />}><Users /></Suspense>
        },
        {
          path: 'campaign',
          element: <Suspense fallback={<LazyFallback />}><Campaign /></Suspense>
        },
        {
          path: 'photos',
          element: <Suspense fallback={<LazyFallback />}><Photos /></Suspense>
        },
        {
          path: 'media-order',
          element: <Suspense fallback={<LazyFallback />}><MediaOrder /></Suspense>
        },
        {
          path: 'invoice',
          element: <Navigate to="/manager/finance" replace />
        },
        {
          path: 'sites/photo',
          element: <Suspense fallback={<LazyFallback />}><Photo /></Suspense>
        },
        {
          path: 'photo-report',
          element: <Suspense fallback={<LazyFallback />}><PhotoReport /></Suspense>
        },
        {
          path: 'finance',
          element: <Suspense fallback={<LazyFallback />}><Finance /></Suspense>
        },
      ]
    },

    // Catch all - redirect to login
    {
      path: '*',
      element: <Navigate to="/" replace />
    }
  ];

  return useRoutes(routes);
};
